import {
	Card,
	Table,
	Button,
	Tag,
	Space,
	Row,
	Col,
	Typography,
	message,
	notification,
	Form,
	Modal,
	Input,
	Select,
} from "antd";
import { useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
	PiPlusDuotone,
	PiPencilDuotone,
	PiTrashDuotone,
	PiEyeDuotone,
	PiMedalDuotone,
} from "react-icons/pi";
import AppContext from "../../store/app.context";
import {
	requestGetUserList,
	requestCreateUser,
	requestUpdateUser,
	requestGetBadgeList,
	requestAwardBadgeToUsers,
} from "../../services/admin.service";
import SearchInput from "../../components/SearchInput";
import dayjs from "dayjs";

const { Title } = Typography;
const { Option } = Select;

function UsersListPage() {
	const context = useContext(AppContext);
	const [searchParams, setSearchParams] = useSearchParams();
	const [loading, setLoading] = useState(false);
	const [users, setUsers] = useState([]);

	// Initialize state from URL parameters
	const [pagination, setPagination] = useState({
		current: parseInt(searchParams.get("page")) || 1,
		pageSize: parseInt(searchParams.get("limit")) || 10,
		total: 0,
	});
	const [filters, setFilters] = useState({
		keyword: searchParams.get("keyword") || "",
	});
	const [modalVisible, setModalVisible] = useState(false);
	const [modalLoading, setModalLoading] = useState(false);
	const [editingUser, setEditingUser] = useState(null);
	const [isEditMode, setIsEditMode] = useState(false);
	const [form] = Form.useForm();

	// Badge-related state
	const [badgeModalVisible, setBadgeModalVisible] = useState(false);
	const [badgeModalLoading, setBadgeModalLoading] = useState(false);
	const [selectedUserForBadge, setSelectedUserForBadge] = useState(null);
	const [badges, setBadges] = useState([]);
	const [badgesLoading, setBadgesLoading] = useState(false);
	const [badgeForm] = Form.useForm();

	useEffect(() => {
		document.title = "Islamiva Console | Users Management";
		fetchUsers();
	}, []);

	useEffect(() => {
		fetchUsers();
	}, [pagination.current, pagination.pageSize, filters]);

	// Update URL parameters when state changes
	useEffect(() => {
		const params = new URLSearchParams();

		// Add pagination parameters
		if (pagination.current > 1) params.set("page", pagination.current.toString());
		if (pagination.pageSize !== 10) params.set("limit", pagination.pageSize.toString());

		// Add filter parameters
		if (filters.keyword) params.set("keyword", filters.keyword);

		// Update URL without triggering navigation
		setSearchParams(params, { replace: true });
	}, [pagination.current, pagination.pageSize, filters, setSearchParams]);

	const fetchUsers = () => {
		setLoading(true);

		// Build filter string from filter object
		const filterParts = [];
		// Add filter fields here as needed
		// Example: if (filters.role) filterParts.push(`role:${filters.role}`);
		// Example: if (filters.status) filterParts.push(`status:${filters.status}`);

		const filterString = filterParts.join(",");

		const params = {
			page: pagination.current,
			limit: pagination.pageSize,
			keyword: filters.keyword,
			filter: filterString,
			order: "_id:desc",
		};

		requestGetUserList(params)
			.then((response) => {
				// Server response structure: { code, message, pagination, data }
				const usersData = response.data.data || [];
				const serverPagination = response.data.pagination || {};

				setUsers(usersData);
				setPagination({
					...pagination,
					total: serverPagination.total || 0,
				});
			})
			.catch((error) => {
				notification.error({
					message: "Error",
					description: "Failed to fetch users",
				});
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const handleSearch = (value) => {
		setFilters({ ...filters, keyword: value });
		setPagination({ ...pagination, current: 1 });
	};

	const handleTableChange = (paginationConfig) => {
		setPagination({
			...pagination,
			current: paginationConfig.current,
			pageSize: paginationConfig.pageSize,
		});
	};

	const handleAddUser = () => {
		form.validateFields().then((values) => {
			setModalLoading(true);
			requestCreateUser(values)
				.then(() => {
					message.success("User created successfully!");
					setModalVisible(false);
					form.resetFields();
					fetchUsers();
				})
				.catch((error) => {
					notification.error({
						message: "Error",
						description: "Failed to create user",
					});
				})
				.finally(() => {
					setModalLoading(false);
				});
		});
	};

	const handleEditUser = (user) => {
		setEditingUser(user);
		setIsEditMode(true);
		setModalVisible(true);
		// Pre-fill form with user data
		form.setFieldsValue({
			name: user.name,
			email: user.email,
			phone: user.phone,
			role: user.role,
		});
	};

	const handleUpdateUser = () => {
		form.validateFields().then((values) => {
			setModalLoading(true);
			// Remove password from values if it's empty (don't update password if not provided)
			const updateValues = { ...values };
			if (!updateValues.password) {
				delete updateValues.password;
			}

			requestUpdateUser(editingUser._id, updateValues)
				.then(() => {
					message.success("User updated successfully!");
					handleCloseModal();
					fetchUsers();
				})
				.catch((error) => {
					notification.error({
						message: "Error",
						description: "Failed to update user",
					});
				})
				.finally(() => {
					setModalLoading(false);
				});
		});
	};

	const handleCloseModal = () => {
		setModalVisible(false);
		setIsEditMode(false);
		setEditingUser(null);
		form.resetFields();
	};

	// Badge-related functions
	const fetchBadges = () => {
		setBadgesLoading(true);
		const params = {
			page: 1,
			limit: 100, // Get all badges
			order: "name:asc",
		};

		requestGetBadgeList(params)
			.then((response) => {
				const badgesData = response.data.data || [];
				setBadges(badgesData);
			})
			.catch((error) => {
				notification.error({
					message: "Error",
					description: "Failed to fetch badges",
				});
			})
			.finally(() => {
				setBadgesLoading(false);
			});
	};

	const handleAwardBadge = (user) => {
		setSelectedUserForBadge(user);
		setBadgeModalVisible(true);
		// Fetch badges when modal opens
		fetchBadges();
	};

	const handleAwardBadgeSubmit = () => {
		badgeForm.validateFields().then((values) => {
			setBadgeModalLoading(true);
			requestAwardBadgeToUsers([selectedUserForBadge._id], values.badgeId)
				.then(() => {
					message.success(`Badge awarded to ${selectedUserForBadge.name} successfully!`);
					handleCloseBadgeModal();
				})
				.catch((error) => {
					notification.error({
						message: "Error",
						description: error.response?.data?.message || "Failed to award badge",
					});
				})
				.finally(() => {
					setBadgeModalLoading(false);
				});
		});
	};

	const handleCloseBadgeModal = () => {
		setBadgeModalVisible(false);
		setSelectedUserForBadge(null);
		setBadges([]);
		badgeForm.resetFields();
	};

	const columns = [
		{
			title: "Name",
			dataIndex: "name",
			key: "name",
			render: (text, record) => (
				<div>
					<div style={{ fontWeight: 500 }}>{text}</div>
					<div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>{record.email}</div>
				</div>
			),
		},
		{
			title: "Total XP",
			dataIndex: "totalXp",
			key: "totalXp",
			width: 100,
			align: "center",
			render: (_, record) => record.totalXP || 0,
		},
		{
			title: "Phone",
			dataIndex: "phone",
			key: "phone",
			width: 150,
		},
		{
			title: "Role",
			dataIndex: "role",
			key: "role",
			width: 100,
			render: (role) => <Tag color={role === "ADMIN" ? "red" : "blue"}>{role}</Tag>,
		},
		{
			title: "Joined At",
			dataIndex: "createdAt",
			key: "createdAt",
			width: 160,
			render: (date, record) => {
				let lastAccessedAt = record.sessions?.length > 0 ? record.sessions[0].lastAccessedAt : null;

				// If lastAccessedAt less than 24 hours ago, show "X hours ago"
				// If lastAccessedAt less than 1 hour ago, show "X minutes ago"
				// If lastAccessedAt yesterday, show "Yesterday HH:mm"
				if (lastAccessedAt) {
					const now = dayjs();
					const accessedAt = dayjs(lastAccessedAt);
					const diffHours = now.diff(accessedAt, "hour");
					const diffMinutes = now.diff(accessedAt, "minute");

					if (diffHours < 24) {
						if (diffHours >= 1) {
							lastAccessedAt = `${diffHours} hours ago`;
						} else if (diffMinutes >= 1) {
							lastAccessedAt = `${diffMinutes} minutes ago`;
						} else {
							lastAccessedAt = "Just now";
						}
					} else if (now.subtract(1, "day").isSame(accessedAt, "day")) {
						lastAccessedAt = `Yesterday ${accessedAt.format("HH:mm")}`;
					} else {
						lastAccessedAt = accessedAt.format("DD MMM YYYY HH:mm");
					}
				}

				return (
					<div>
						<span>{dayjs(date).format("DD MMM YYYY HH:mm")}</span>
						{lastAccessedAt && (
							<div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>
								Last Active: {lastAccessedAt}
							</div>
						)}
					</div>
				);
			},
		},
		{
			title: "Actions",
			key: "actions",
			width: 200,
			align: "right",
			render: (_, record) => (
				<Space size="small">
					<Button type="text" icon={<PiEyeDuotone />} title="View Details" />
					<Button
						type="text"
						icon={<PiPencilDuotone />}
						title="Edit User"
						onClick={() => handleEditUser(record)}
					/>
					<Button
						type="text"
						icon={<PiMedalDuotone />}
						title="Award Badge"
						style={{ color: "#faad14" }}
						onClick={() => handleAwardBadge(record)}
					/>
					<Button type="text" danger icon={<PiTrashDuotone />} title="Delete User" />
				</Space>
			),
		},
	];

	return (
		<div>
			<div style={{ marginBottom: 24 }}>
				<Title level={3}>Users Management</Title>
			</div>

			{/* Users Table */}
			<Card
				title="User List"
				extra={
					<Button type="primary" icon={<PiPlusDuotone />} onClick={() => setModalVisible(true)}>
						Tambah Pengguna
					</Button>
				}>
				<Table
					columns={columns}
					dataSource={users}
					rowKey="_id"
					loading={loading}
					pagination={{
						current: pagination.current,
						pageSize: pagination.pageSize,
						total: pagination.total,
						showSizeChanger: true,
						showQuickJumper: true,
						showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} users`,
					}}
					onChange={handleTableChange}
					scroll={{ x: 1000 }}
				/>
			</Card>

			{/* Add/Edit User Modal */}
			<Modal
				title={isEditMode ? "Edit User" : "Add User"}
				open={modalVisible}
				onOk={isEditMode ? handleUpdateUser : handleAddUser}
				onCancel={handleCloseModal}
				confirmLoading={modalLoading}
				width={600}>
				<Form form={form} layout="vertical" style={{ marginTop: 16 }}>
					<Form.Item
						name="name"
						label="Full Name"
						rules={[{ required: true, message: "Please enter full name" }]}>
						<Input placeholder="John Doe" />
					</Form.Item>
					<Form.Item
						name="email"
						label="Email Address"
						rules={[
							{ required: true, message: "Please enter email address" },
							{ type: "email", message: "Please enter a valid email" },
						]}>
						<Input placeholder="john@example.com" />
					</Form.Item>
					<Form.Item
						name="phone"
						label="Phone Number"
						rules={[{ required: true, message: "Please enter phone number" }]}>
						<Input placeholder="+6281234567890" />
					</Form.Item>
					<Form.Item
						name="password"
						label={isEditMode ? "Password (leave empty to keep current)" : "Password"}
						rules={[
							{ required: !isEditMode, message: "Please enter password" },
							{ min: 6, message: "Password must be at least 6 characters" },
						]}>
						<Input.Password
							placeholder={isEditMode ? "Leave empty to keep current password" : "Password"}
						/>
					</Form.Item>
					<Form.Item
						name="role"
						label="User Role"
						rules={[{ required: true, message: "Please select user role" }]}>
						<Select placeholder="Select role">
							<Option value="USER">User</Option>
							<Option value="CONTRIBUTOR">Contributor</Option>
							<Option value="ADMIN">Admin</Option>
						</Select>
					</Form.Item>
				</Form>
			</Modal>

			{/* Award Badge Modal */}
			<Modal
				title={`Award Badge to ${selectedUserForBadge?.name || ""}`}
				open={badgeModalVisible}
				onOk={handleAwardBadgeSubmit}
				onCancel={handleCloseBadgeModal}
				confirmLoading={badgeModalLoading}
				width={500}>
				<div style={{ marginBottom: 16 }}>
					<p>
						<strong>User:</strong> {selectedUserForBadge?.name} ({selectedUserForBadge?.email})
					</p>
					<p>
						<strong>Role:</strong>{" "}
						<Tag color={selectedUserForBadge?.role === "ADMIN" ? "red" : "blue"}>
							{selectedUserForBadge?.role}
						</Tag>
					</p>
				</div>
				<Form form={badgeForm} layout="vertical">
					<Form.Item
						name="badgeId"
						label="Select Badge to Award"
						rules={[{ required: true, message: "Please select a badge" }]}>
						<Select
							placeholder="Choose a badge"
							loading={badgesLoading}
							showSearch
							optionFilterProp="children">
							{badges.map((badge) => (
								<Option key={badge._id} value={badge._id}>
									<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
										{badge.icon && <span>{badge.icon}</span>}
										<span>{badge.name}</span>
										{badge.description && (
											<span style={{ fontSize: 12, color: "#666" }}>- {badge.description}</span>
										)}
									</div>
								</Option>
							))}
						</Select>
					</Form.Item>
				</Form>
			</Modal>
		</div>
	);
}

export default UsersListPage;
